/**
 * Client for de-hub's shared `app_support` module — user feedback intake,
 * planned-outage announcements, and the notification bell they deliver
 * through.
 *
 * Every call carries `app=<APP_KEY>` (see src/config/appSupport.js). The
 * server scopes queues, announcements and notifications by it, so a missing
 * `VITE_APP_KEY` shows up as "my queue is empty" rather than an error —
 * which is why the admin screen surfaces the misconfiguration explicitly.
 *
 * Each method takes `{ token, userOid, ... }` and returns the raw axios
 * response; callers read `resp?.data?.data` per the standard de-hub envelope.
 *
 * VISIBILITY IS THE SERVER'S JOB. An admin's feedback list comes back with
 * every ticket for this app; everyone else's comes back with only their own,
 * and a ticket they may not see 404s. There is deliberately no client-side
 * filtering here to mirror that.
 */
import dataService from "./dataservice";
import { APP_KEY, API_BASE as BASE } from "../config/appSupport";

// Every endpoint is app-scoped; fold the key in one place rather than at
// twelve call sites.
const withApp = (params = {}) => ({ app: APP_KEY, ...params });

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

/**
 * POST /api/app_support/feedback
 *
 * `context` is the browser-captured diagnostic block from
 * `utils/clientContext.js` — timestamps, Clarity session ids, page URL, user
 * agent, viewport. The server model is `extra="forbid"`, so build it with
 * `captureClientContext()` rather than by hand: a stray key is a 422.
 *
 * Identity is not sent — the server takes `reported_by` from the token.
 */
const createFeedback = ({
  token,
  userOid,
  kind,
  title,
  description,
  severity,
  context,
}) => {
  const body = { app: APP_KEY, kind, title, description };
  if (severity) body.severity = severity;
  if (context) body.context = context;
  return dataService.postEndpoint(`${BASE}/feedback`, token, userOid, body);
};

/**
 * GET /api/app_support/feedback
 *
 * Newest first, server-scoped. `mine` narrows an ADMIN to their own tickets
 * and is a no-op for everyone else, who are already narrowed.
 */
const listFeedback = ({ token, userOid, status, kind, openOnly, mine, limit }) => {
  const params = withApp();
  if (status) params.status = status;
  if (kind) params.kind = kind;
  if (openOnly !== undefined) params.open_only = openOnly;
  if (mine !== undefined) params.mine = mine;
  if (limit !== undefined) params.limit = limit;
  return dataService.request("get", `${BASE}/feedback`, token, userOid, undefined, {
    params,
  });
};

/** GET /api/app_support/feedback/stats — `{ open, total, by_status, by_kind }`. */
const getFeedbackStats = ({ token, userOid }) =>
  dataService.request("get", `${BASE}/feedback/stats`, token, userOid, undefined, {
    params: withApp(),
  });

/**
 * GET /api/app_support/feedback/{id}
 *
 * One ticket with `comments` and `attachments`. Admin-only internal comments
 * are stripped server-side. A 404 covers both "no such ticket" and "not
 * yours" — do not report the difference, there isn't one to report.
 */
const getFeedback = ({ token, userOid, id }) =>
  dataService.request(
    "get",
    `${BASE}/feedback/${encodeURIComponent(id)}`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

/**
 * PUT /api/app_support/feedback/{id} — admin triage, partial.
 *
 * `title` / `description` are not updatable by design; the reported text is
 * evidence. `issue_id` links the ticket to an engineering-tracker issue once
 * it is escalated.
 */
const updateFeedback = ({ token, userOid, id, updates }) =>
  dataService.request(
    "put",
    `${BASE}/feedback/${encodeURIComponent(id)}`,
    token,
    userOid,
    updates,
    { params: withApp() }
  );

/**
 * POST /api/app_support/feedback/{id}/comments
 *
 * `isInternal` is an admin-only note; the server forces it false for
 * non-admins, so passing it from a non-admin surface is harmless — but don't
 * offer the control there.
 */
const addFeedbackComment = ({ token, userOid, id, body, isInternal }) =>
  dataService.request(
    "post",
    `${BASE}/feedback/${encodeURIComponent(id)}/comments`,
    token,
    userOid,
    { body, is_internal: Boolean(isInternal) },
    { params: withApp() }
  );

/**
 * POST /api/app_support/feedback/{id}/attachments (multipart)
 *
 * Content-Type is left unset so axios picks the multipart boundary — setting
 * it by hand without one breaks FastAPI's parser.
 */
const uploadFeedbackAttachment = ({ token, userOid, id, file }) => {
  const form = new FormData();
  form.append("file", file);
  return dataService.request(
    "post",
    `${BASE}/feedback/${encodeURIComponent(id)}/attachments`,
    token,
    userOid,
    form,
    { params: withApp() }
  );
};

/** GET .../feedback_attachments/{id}/url — short-lived SAS; never cache it. */
const getFeedbackAttachmentUrl = ({ token, userOid, id }) =>
  dataService.request(
    "get",
    `${BASE}/feedback_attachments/${encodeURIComponent(id)}/url`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

const deleteFeedbackAttachment = ({ token, userOid, id }) =>
  dataService.request(
    "delete",
    `${BASE}/feedback_attachments/${encodeURIComponent(id)}`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

/**
 * GET /api/app_support/announcements/active
 *
 * What this user should be showing right now — for this app AND for the `*`
 * all-apps sentinel, since a de-hub outage affects every front end. Each row
 * carries the caller's own `acknowledged` / `dismissed_phase`.
 *
 * Cache the result (see `useAnnouncements`): if the API is unreachable during
 * a window that was already announced, the cached copy is what keeps the
 * banner on screen, which is exactly when it matters.
 */
const listActiveAnnouncements = ({ token, userOid }) =>
  dataService.request(
    "get",
    `${BASE}/announcements/active`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

/**
 * GET /api/app_support/announcements/history
 *
 * Published announcements including expired ones — the "What's new" archive.
 * Open to everyone; drafts and not-yet-published notices stay hidden.
 *
 * Distinct from `/active`, which returns only what belongs on screen right
 * now. `active` deliberately stops showing a notice once it expires or is
 * dismissed — correct for an outage banner, wrong for a release note somebody
 * wants to catch up on.
 *
 * @param {string} [kind] — pass "release" for just the product news.
 */
const listAnnouncementHistory = ({ token, userOid, kind, limit }) => {
  const params = withApp();
  if (kind) params.kind = kind;
  if (limit !== undefined) params.limit = limit;
  return dataService.request(
    "get",
    `${BASE}/announcements/history`,
    token,
    userOid,
    undefined,
    { params }
  );
};

/** POST .../announcements/{id}/ack — modal closed. Permanent. */
const acknowledgeAnnouncement = ({ token, userOid, id }) =>
  dataService.postEndpoint(
    `${BASE}/announcements/${encodeURIComponent(id)}/ack`,
    token,
    userOid,
    {}
  );

/**
 * POST .../announcements/{id}/dismiss — banner closed, in `phase`.
 * Per-phase on purpose: dismissing a notice days early must not suppress it
 * once the window is imminent.
 */
const dismissAnnouncement = ({ token, userOid, id, phase }) =>
  dataService.postEndpoint(
    `${BASE}/announcements/${encodeURIComponent(id)}/dismiss`,
    token,
    userOid,
    { phase }
  );

/** GET /api/app_support/announcements — admin list, incl. drafts. */
const listAllAnnouncements = ({ token, userOid, includeExpired = true }) =>
  dataService.request("get", `${BASE}/announcements`, token, userOid, undefined, {
    params: withApp({ include_expired: includeExpired }),
  });

/**
 * POST /api/app_support/announcements
 *
 * `published: false` (the default) saves a draft nobody can see. Publishing —
 * here or later — is what notifies, and only on the false→true transition.
 */
const createAnnouncement = ({ token, userOid, announcement }) =>
  dataService.postEndpoint(`${BASE}/announcements`, token, userOid, {
    app: APP_KEY,
    ...announcement,
  });

const updateAnnouncement = ({ token, userOid, id, updates }) =>
  dataService.updateEndpoint(
    `${BASE}/announcements/${encodeURIComponent(id)}`,
    token,
    userOid,
    updates
  );

/** DELETE — hard. To retract without losing the text, set published:false. */
const deleteAnnouncement = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/announcements/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

const listNotifications = ({ token, userOid, unread, limit }) => {
  const params = withApp();
  if (unread !== undefined) params.unread = unread;
  if (limit !== undefined) params.limit = limit;
  return dataService.request(
    "get",
    `${BASE}/notifications`,
    token,
    userOid,
    undefined,
    { params }
  );
};

const getUnreadCount = ({ token, userOid }) =>
  dataService.request(
    "get",
    `${BASE}/notifications/unread_count`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

const markNotificationRead = ({ token, userOid, id }) =>
  dataService.request(
    "post",
    `${BASE}/notifications/${encodeURIComponent(id)}/read`,
    token,
    userOid,
    {},
    { params: withApp() }
  );

const markAllNotificationsRead = ({ token, userOid }) =>
  dataService.request(
    "post",
    `${BASE}/notifications/read_all`,
    token,
    userOid,
    {},
    { params: withApp() }
  );

/**
 * GET /api/app_support/notifications/prefs
 *
 * `event_type -> enabled`, always complete. Un-mutable event types (the
 * planned-outage notices) are absent by design — render a switch per key and
 * you will never draw one the server would refuse.
 */
const getNotificationPrefs = ({ token, userOid }) =>
  dataService.request(
    "get",
    `${BASE}/notifications/prefs`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

/** PUT — partial; returns the complete map after the write. */
const updateNotificationPrefs = ({ token, userOid, prefs }) =>
  dataService.request(
    "put",
    `${BASE}/notifications/prefs`,
    token,
    userOid,
    { prefs },
    { params: withApp() }
  );

/**
 * GET /api/app_support/notifications/email_prefs
 *
 * Same shape as `/notifications/prefs` (`event_type -> enabled`), but it only
 * holds the event types this user may toggle for EMAIL. An absent key means
 * "no email switch for this event", not "off".
 */
const getNotificationEmailPrefs = ({ token, userOid }) =>
  dataService.request(
    "get",
    `${BASE}/notifications/email_prefs`,
    token,
    userOid,
    undefined,
    { params: withApp() }
  );

/** PUT — partial upsert; 422 for an event type that isn't toggleable. */
const updateNotificationEmailPrefs = ({ token, userOid, prefs }) =>
  dataService.request(
    "put",
    `${BASE}/notifications/email_prefs`,
    token,
    userOid,
    { prefs },
    { params: withApp() }
  );

// ---------------------------------------------------------------------------
// UI settings — app-wide look (`theme_preset`), flipped from Admin → Appearance
// ---------------------------------------------------------------------------

/**
 * GET /api/app_support/ui_settings
 *
 * `data` is every setting with defaults filled in (`{ theme_preset, density }`);
 * `can_edit` says whether this caller may change them.
 *
 * `cache: false`: axios-cache-interceptor would otherwise answer a reload for
 * five minutes with the value from BEFORE an admin's save, and put the old
 * look back.
 */
const getUiSettings = ({ token, userOid }) =>
  dataService.request("get", `${BASE}/ui_settings`, token, userOid, undefined, {
    params: withApp(),
    cache: false,
  });

/** PUT /api/app_support/ui_settings/{key} — admin only; returns the full map. */
const updateUiSetting = ({ token, userOid, key, value }) =>
  dataService.request(
    "put",
    `${BASE}/ui_settings/${encodeURIComponent(key)}`,
    token,
    userOid,
    { value },
    { params: withApp() }
  );

const appSupportService = {
  createFeedback,
  listFeedback,
  getFeedbackStats,
  getFeedback,
  updateFeedback,
  addFeedbackComment,
  uploadFeedbackAttachment,
  getFeedbackAttachmentUrl,
  deleteFeedbackAttachment,
  listActiveAnnouncements,
  listAnnouncementHistory,
  acknowledgeAnnouncement,
  dismissAnnouncement,
  listAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  getNotificationPrefs,
  updateNotificationPrefs,
  getNotificationEmailPrefs,
  updateNotificationEmailPrefs,
  getUiSettings,
  updateUiSetting,
};

export default appSupportService;
